/**
 * DropZone — react-dropzone wrapper for CSV/XLSX uploads.
 * Accepts one file at a time; ≤10MB enforced client-side (server also caps).
 */

import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { UploadCloud, FileText } from 'lucide-react'
import { AgentBubble } from './AgentBubble'

interface DropZoneProps {
  onFile: (file: File) => void
  disabled?: boolean
}

const MAX_BYTES = 10 * 1024 * 1024

export function DropZone({ onFile, disabled }: DropZoneProps) {
  const onDrop = useCallback(
    (accepted: File[]) => {
      const f = accepted[0]
      if (!f) return
      if (f.size > MAX_BYTES) {
        alert('That file is larger than 10MB. Split it first or bail.')
        return
      }
      onFile(f)
    },
    [onFile],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    multiple: false,
    disabled,
  })

  return (
    <div className="w-full max-w-xl space-y-4">
      <AgentBubble from="agent">
                Drop the file the ticketing company sent you. CSV or Excel — I'll
        figure out the rest.
      </AgentBubble>

      <div
        {...getRootProps()}
        className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-12 text-center transition-colors ${
          isDragActive
            ? 'border-carnival-400 bg-carnival-50 dark:border-carnival-300 dark:bg-carnival-400/10'
            : 'border-slate-300 bg-white hover:border-carnival-300 dark:border-slate-600 dark:bg-slate-800 dark:hover:border-carnival-400'
        } ${disabled ? 'pointer-events-none opacity-50' : ''}`}
      >
        <input {...getInputProps()} data-testid="smart-import-file" />
        <UploadCloud className="mb-3 h-8 w-8 text-carnival-400" />
        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {isDragActive ? 'Drop it' : 'Drag in a CSV or XLSX'}
        </div>
        <div className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
          <FileText className="h-3 w-3" />
                    or click to pick · max 10MB
        </div>
      </div>
    </div>
  )
}
